/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";
import {
  getTagScoreDecoder,
  getTagScoreEncoder,
  type TagScore,
  type TagScoreArgs,
} from "../types";

export const UPDATE_REPUTATION_TAGS_DISCRIMINATOR = new Uint8Array([
  206, 96, 254, 186, 129, 180, 103, 198,
]);

export function getUpdateReputationTagsDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_REPUTATION_TAGS_DISCRIMINATOR,
  );
}

export type UpdateReputationTagsInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountReputationMetrics extends string | AccountMeta<string> = string,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountReputationMetrics extends string
        ? WritableAccount<TAccountReputationMetrics>
        : TAccountReputationMetrics,
      TAccountAgent extends string
        ? WritableAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateReputationTagsInstructionData = {
  discriminator: ReadonlyUint8Array;
  skillTags: Array<string>;
  behaviorTags: Array<string>;
  complianceTags: Array<string>;
  tagScores: Array<TagScore>;
};

export type UpdateReputationTagsInstructionDataArgs = {
  skillTags: Array<string>;
  behaviorTags: Array<string>;
  complianceTags: Array<string>;
  tagScores: Array<TagScoreArgs>;
};

export function getUpdateReputationTagsInstructionDataEncoder(): Encoder<UpdateReputationTagsInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      [
        "skillTags",
        getArrayEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      [
        "behaviorTags",
        getArrayEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      [
        "complianceTags",
        getArrayEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["tagScores", getArrayEncoder(getTagScoreEncoder())],
    ]),
    (value) => ({
      ...value,
      discriminator: UPDATE_REPUTATION_TAGS_DISCRIMINATOR,
    }),
  );
}

export function getUpdateReputationTagsInstructionDataDecoder(): Decoder<UpdateReputationTagsInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    [
      "skillTags",
      getArrayDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    [
      "behaviorTags",
      getArrayDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    [
      "complianceTags",
      getArrayDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["tagScores", getArrayDecoder(getTagScoreDecoder())],
  ]);
}

export function getUpdateReputationTagsInstructionDataCodec(): Codec<
  UpdateReputationTagsInstructionDataArgs,
  UpdateReputationTagsInstructionData
> {
  return combineCodec(
    getUpdateReputationTagsInstructionDataEncoder(),
    getUpdateReputationTagsInstructionDataDecoder(),
  );
}

export type UpdateReputationTagsAsyncInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics?: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (can be the agent owner or authorized updater) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  skillTags: UpdateReputationTagsInstructionDataArgs["skillTags"];
  behaviorTags: UpdateReputationTagsInstructionDataArgs["behaviorTags"];
  complianceTags: UpdateReputationTagsInstructionDataArgs["complianceTags"];
  tagScores: UpdateReputationTagsInstructionDataArgs["tagScores"];
};

export async function getUpdateReputationTagsInstructionAsync<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateReputationTagsAsyncInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  UpdateReputationTagsInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.reputationMetrics.value) {
    accounts.reputationMetrics.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 112, 117, 116, 97, 116, 105, 111, 110, 95, 109, 101, 116,
            114, 105, 99, 115,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.agent.value)),
      ],
    });
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateReputationTagsInstructionDataEncoder().encode(
      args as UpdateReputationTagsInstructionDataArgs,
    ),
    programAddress,
  } as UpdateReputationTagsInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >);
}

export type UpdateReputationTagsInput<
  TAccountReputationMetrics extends string = string,
  TAccountAgent extends string = string,
  TAccountAuthority extends string = string,
  TAccountClock extends string = string,
> = {
  /** Reputation metrics account */
  reputationMetrics: Address<TAccountReputationMetrics>;
  /** Agent account */
  agent: Address<TAccountAgent>;
  /** Authority (can be the agent owner or authorized updater) */
  authority: TransactionSigner<TAccountAuthority>;
  /** Clock for timestamps */
  clock?: Address<TAccountClock>;
  skillTags: UpdateReputationTagsInstructionDataArgs["skillTags"];
  behaviorTags: UpdateReputationTagsInstructionDataArgs["behaviorTags"];
  complianceTags: UpdateReputationTagsInstructionDataArgs["complianceTags"];
  tagScores: UpdateReputationTagsInstructionDataArgs["tagScores"];
};

export function getUpdateReputationTagsInstruction<
  TAccountReputationMetrics extends string,
  TAccountAgent extends string,
  TAccountAuthority extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: UpdateReputationTagsInput<
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): UpdateReputationTagsInstruction<
  TProgramAddress,
  TAccountReputationMetrics,
  TAccountAgent,
  TAccountAuthority,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    reputationMetrics: {
      value: input.reputationMetrics ?? null,
      isWritable: true,
    },
    agent: { value: input.agent ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.reputationMetrics),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.clock),
    ],
    data: getUpdateReputationTagsInstructionDataEncoder().encode(
      args as UpdateReputationTagsInstructionDataArgs,
    ),
    programAddress,
  } as UpdateReputationTagsInstruction<
    TProgramAddress,
    TAccountReputationMetrics,
    TAccountAgent,
    TAccountAuthority,
    TAccountClock
  >);
}

export type ParsedUpdateReputationTagsInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Reputation metrics account */
    reputationMetrics: TAccountMetas[0];
    /** Agent account */
    agent: TAccountMetas[1];
    /** Authority (can be the agent owner or authorized updater) */
    authority: TAccountMetas[2];
    /** Clock for timestamps */
    clock: TAccountMetas[3];
  };
  data: UpdateReputationTagsInstructionData;
};

export function parseUpdateReputationTagsInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateReputationTagsInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      reputationMetrics: getNextAccount(),
      agent: getNextAccount(),
      authority: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getUpdateReputationTagsInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
